import { useState, useEffect, useRef } from "react";
import Image from "next/image";
import { InstagramLogoIcon, XLogo } from "@phosphor-icons/react/dist/ssr";
import { CaretDown } from "@phosphor-icons/react/dist/ssr";
import Link from "next/link";
import { useLanguage } from "@/contexts/LanguageContext";
import { useRegion } from "@/contexts/RegionContext";
import { useRouter } from "next/router";

interface NavigationProps {
  noMenu?: boolean;
  noGradient?: boolean;
}

export default function Navigation({ noMenu, noGradient }: NavigationProps) {
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isWhoWeServeOpen, setIsWhoWeServeOpen] = useState(false);
  const whoWeServeCloseTimeoutRef = useRef<number | null>(null);
  const { language, setLanguage, t } = useLanguage();
  const { region, isLoading } = useRegion();
  const router = useRouter();
  const basePrefix = language === "fr" ? "/fr" : "";
  const whoWeServeItems = [
    { key: "dietitians", href: `${basePrefix}/for-dietitians` },
    { key: "nutritionists", href: `${basePrefix}/for-nutritionists` },
    { key: "healthCoaches", href: `${basePrefix}/for-health-coaches` },
    { key: "medicalDoctors", href: `${basePrefix}/for-medical-doctors` },
    { key: "personalTrainers", href: `${basePrefix}/for-personal-trainers` },
  ];

  const openWhoWeServe = () => {
    if (whoWeServeCloseTimeoutRef.current) {
      window.clearTimeout(whoWeServeCloseTimeoutRef.current);
      whoWeServeCloseTimeoutRef.current = null;
    }
    setIsWhoWeServeOpen(true);
  };

  const closeWhoWeServe = (delayMs: number = 120) => {
    if (whoWeServeCloseTimeoutRef.current) {
      window.clearTimeout(whoWeServeCloseTimeoutRef.current);
    }
    whoWeServeCloseTimeoutRef.current = window.setTimeout(() => {
      setIsWhoWeServeOpen(false);
      whoWeServeCloseTimeoutRef.current = null;
    }, delayMs);
  };
  const scrollToSection = (sectionId: string) => {
    // Fermer le menu mobile si ouvert
    setIsMobileMenuOpen(false);

    // Si on n'est pas sur la page d'accueil, naviguer d'abord vers celle-ci
    if (router.pathname !== "/") {
      router.push(`/#${sectionId}`);
      return;
    }

    // Si on est déjà sur la page d'accueil, scroller directement
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      // Si on est en haut de la page, toujours afficher la navigation
      if (currentScrollY < 10) {
        setIsVisible(true);
      } else {
        // Si on scroll vers le bas, cacher la navigation
        if (currentScrollY > lastScrollY && currentScrollY > 100) {
          setIsVisible(false);
        }
        // Si on scroll vers le haut, afficher la navigation
        else if (currentScrollY < lastScrollY) {
          setIsVisible(true);
        }
      }

      setLastScrollY(currentScrollY);
    };

    // Throttle la fonction pour optimiser les performances
    let ticking = false;
    const throttledHandleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener("scroll", throttledHandleScroll);

    return () => window.removeEventListener("scroll", throttledHandleScroll);
  }, [lastScrollY]);

  return (
    <nav
      role="banner"
      aria-label="Main navigation"
      className={`${
        noGradient ? "" : "bg-gradient-to-b from-white to-transparent"
      } max-w-6xl gap-4 md:gap-8 mx-auto flex items-center justify-between py-6 px-8 xl:px-0 fixed top-0 z-50 w-full transition-transform duration-300 ease-in-out ${
        isVisible ? "transform translate-y-0" : "transform -translate-y-full"
      } relative`}
    >
      <Link href="/" aria-label="Featcal homepage">
        <Image
          src="/images/logo.svg"
          alt="Featcal logo"
          width={120}
          height={120}
          priority
          className="max-md:w-[150px]"
        />
      </Link>
      {!noMenu && (
        <>
          {/* Menu desktop */}
          <ul className="hidden md:flex items-center justify-center gap-6 bg-white border border-blue-200 rounded-full px-6 py-3.5 font-semibold shadow-xl shadow-blue-200/20">
            <li>
              <button
                type="button"
                onMouseEnter={openWhoWeServe}
                onMouseLeave={() => closeWhoWeServe()}
                onFocus={openWhoWeServe}
                onClick={() => setIsWhoWeServeOpen((v) => !v)}
                aria-haspopup="menu"
                aria-expanded={isWhoWeServeOpen}
                className="hover:text-blue-600 transition-colors duration-200 inline-flex items-center gap-2"
              >
                {t("navigation.whoWeServe")}
                <CaretDown
                  className={`transition-transform duration-200 ${
                    isWhoWeServeOpen ? "rotate-180" : ""
                  }`}
                />
              </button>
            </li>
            <li>
              <button
                onClick={() => scrollToSection("app-features")}
                className="hover:text-blue-600 transition-colors duration-200"
              >
                {t("navigation.appFeatures")}
              </button>
            </li>
            <li>
              <button
                onClick={() =>
                  router.push(language === "fr" ? "/fr/pricing" : "/pricing")
                }
                className="hover:text-blue-600 transition-colors duration-200"
              >
                {t("navigation.pricing")}
              </button>
            </li>
            <li>
              <button
                onClick={() =>
                  router.push(language === "fr" ? "/fr/blog" : "/blog")
                }
                className="hover:text-blue-600 transition-colors duration-200"
              >
                {t("navigation.blog")}
              </button>
            </li>
            <li>
              <button
                onClick={() => scrollToSection("faq")}
                className="hover:text-blue-600 transition-colors duration-200"
              >
                {t("navigation.faq")}
              </button>
            </li>
          </ul>

          <div
            className={`hidden md:block absolute left-0 right-0 top-full pt-3 ${
              isWhoWeServeOpen ? "pointer-events-auto" : "pointer-events-none"
            }`}
            onMouseEnter={openWhoWeServe}
            onMouseLeave={() => closeWhoWeServe()}
          >
            <div
              role="menu"
              aria-label={t("navigation.whoWeServe")}
              className={`mx-auto w-full max-w-6xl px-8 xl:px-0 transition-all duration-150 ${
                isWhoWeServeOpen
                  ? "opacity-100 translate-y-0"
                  : "opacity-0 -translate-y-1"
              }`}
            >
              <div className="rounded-3xl border border-slate-200 bg-white shadow-2xl shadow-blue-200/30 p-4">
                <div className="grid grid-cols-12 gap-4">
                  <div className="col-span-5 rounded-2xl bg-slate-50 border border-slate-200 p-6">
                    <div className="text-3xl font-semibold text-slate-900 mb-6">
                      {t("navigation.profession")}
                    </div>
                    <div className="grid grid-cols-2 gap-x-6 gap-y-3">
                      {whoWeServeItems.map((item) => (
                        <Link
                          key={item.key}
                          href={item.href}
                          role="menuitem"
                          className="text-slate-900 hover:text-blue-700 transition-colors duration-150 text-base"
                          onClick={() => setIsWhoWeServeOpen(false)}
                        >
                          {t(`navigation.${item.key}`)}
                        </Link>
                      ))}
                    </div>
                  </div>

                  <div className="col-span-7 rounded-2xl bg-gradient-to-br from-emerald-50 to-blue-50 border border-emerald-100 p-6">
                    <div className="grid grid-cols-2 gap-4">
                      {[
                        {
                          title:
                            language === "fr"
                              ? "Cabinets & libéraux"
                              : "Solo practices",
                          href:
                            whoWeServeItems[0]?.href ??
                            `${basePrefix}/for-dietitians`,
                          image: "/images/my-day.webp",
                        },
                        {
                          title:
                            language === "fr"
                              ? "Cliniques & équipes"
                              : "Clinics & teams",
                          href: `${basePrefix}/for-companies`,
                          image: "/images/customer-admin.webp",
                        },
                      ].map((card) => (
                        <Link
                          key={card.title}
                          href={card.href}
                          className="group rounded-2xl bg-white/70 hover:bg-white transition-colors duration-200 border border-white/70 hover:border-white shadow-lg shadow-black/5 overflow-hidden"
                          onClick={() => setIsWhoWeServeOpen(false)}
                        >
                          <div className="relative h-[120px] w-full">
                            <Image
                              src={card.image}
                              alt={card.title}
                              fill
                              sizes="(max-width: 768px) 0px, 220px"
                              className="object-cover"
                            />
                          </div>
                          <div className="p-4 text-slate-900 font-semibold group-hover:text-blue-800">
                            {card.title}
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Bouton hamburger mobile */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="Toggle menu"
            className="md:hidden bg-white rounded-full p-2 border border-slate-200 text-slate-900 hover:bg-slate-100 transition-colors duration-200"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              {isMobileMenuOpen ? (
                <path d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>
        </>
      )}
      <div className="flex items-center gap-2 max-md:hidden">
        {!isLoading && region !== "US" && (
          <button
            onClick={() => setLanguage(language === "fr" ? "en" : "fr")}
            aria-label={`Switch to ${language === "fr" ? "English" : "French"}`}
            className="bg-white rounded-full px-3 py-2 border border-slate-200 text-slate-900 hover:bg-slate-100 transition-colors duration-200 text-xs font-medium"
          >
            {language === "fr" ? "EN" : "FR"}
          </button>
        )}
        <Link
          target="_blank"
          href="https://x.com/featcal_com"
          aria-label="Follow Featcal on Twitter"
          className="bg-white rounded-full p-2 border border-slate-200 text-slate-900 hover:bg-slate-100 transition-colors duration-200"
        >
          <XLogo />
        </Link>
        <Link
          target="_blank"
          href="https://www.instagram.com/featcal/"
          aria-label="Follow Featcal on Instagram"
          className="bg-white rounded-full p-2 border border-slate-200 text-slate-900 hover:bg-slate-100 transition-colors duration-200"
        >
          <InstagramLogoIcon />
        </Link>
      </div>

      {/* Menu mobile overlay */}
      {!noMenu && isMobileMenuOpen && (
        <div className="md:hidden fixed inset-0 bg-white z-[100]">
          {/* Header avec logo et bouton fermer */}
          <div className="flex items-center justify-between py-6 px-8 bg-white relative z-10">
            <Link href="/" aria-label="Featcal homepage">
              <Image
                src="/images/logo.svg"
                alt="Featcal logo"
                width={120}
                height={120}
                priority
                className="w-[150px]"
              />
            </Link>
            <button
              onClick={() => setIsMobileMenuOpen(false)}
              aria-label="Close menu"
              className="bg-white rounded-full p-2 border border-slate-200 text-slate-900 hover:bg-slate-100 transition-colors duration-200"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Contenu du menu */}
          <div className="flex flex-col items-center justify-center p-8 mt-16 bg-white fixed top-0 left-0 w-full z-10 shadow-lg">
            <ul className="flex flex-col items-center gap-6 w-full">
              <li className="w-full">
                <div className="w-full">
                  <div className="text-sm font-semibold text-slate-700 mb-2 px-2">
                    {t("navigation.whoWeServe")}
                  </div>
                  <div className="grid grid-cols-1 gap-3">
                    {whoWeServeItems.map((item) => (
                      <Link
                        key={item.key}
                        href={item.href}
                        onClick={() => setIsMobileMenuOpen(false)}
                        className="w-full bg-white border border-blue-200 rounded-full px-6 py-4 font-semibold shadow-lg shadow-blue-200/20 hover:bg-blue-50 transition-colors duration-200 text-lg text-center"
                      >
                        {t(`navigation.${item.key}`)}
                      </Link>
                    ))}
                  </div>
                </div>
              </li>
              <li className="w-full">
                <button
                  onClick={() => scrollToSection("app-features")}
                  className="w-full bg-white border border-blue-200 rounded-full px-6 py-4 font-semibold shadow-lg shadow-blue-200/20 hover:bg-blue-50 transition-colors duration-200 text-lg"
                >
                  {t("navigation.appFeatures")}
                </button>
              </li>
              <li className="w-full">
                <button
                  onClick={() => {
                    router.push(language === "fr" ? "/fr/pricing" : "/pricing");
                    setIsMobileMenuOpen(false);
                  }}
                  className="w-full bg-white border border-blue-200 rounded-full px-6 py-4 font-semibold shadow-lg shadow-blue-200/20 hover:bg-blue-50 transition-colors duration-200 text-lg"
                >
                  {t("navigation.pricing")}
                </button>
              </li>
              <li className="w-full">
                <button
                  onClick={() => {
                    router.push(language === "fr" ? "/fr/blog" : "/blog");
                    setIsMobileMenuOpen(false);
                  }}
                  className="w-full bg-white border border-blue-200 rounded-full px-6 py-4 font-semibold shadow-lg shadow-blue-200/20 hover:bg-blue-50 transition-colors duration-200 text-lg"
                >
                  {t("navigation.blog")}
                </button>
              </li>
              <li className="w-full">
                <button
                  onClick={() => scrollToSection("faq")}
                  className="w-full bg-white border border-blue-200 rounded-full px-6 py-4 font-semibold shadow-lg shadow-blue-200/20 hover:bg-blue-50 transition-colors duration-200 text-lg"
                >
                  {t("navigation.faq")}
                </button>
              </li>
            </ul>

            <div className="flex flex-col items-center gap-4 w-full mt-12">
              {!isLoading && region !== "US" && (
                <button
                  onClick={() =>
                    setLanguage(language === "fr" ? "en" : "fr") &&
                    setIsMobileMenuOpen(false)
                  }
                  aria-label={`Switch to ${
                    language === "fr" ? "English" : "French"
                  }`}
                  className="w-full bg-white rounded-full px-6 py-4 border border-slate-200 text-slate-900 hover:bg-slate-100 transition-colors duration-200 font-medium"
                >
                  {language === "fr" ? "EN" : "FR"}
                </button>
              )}
              <div className="flex items-center gap-4 justify-center w-full">
                <Link
                  target="_blank"
                  href="https://x.com/featcal_com"
                  aria-label="Follow Featcal on Twitter"
                  className="bg-white rounded-full p-4 border border-slate-200 text-slate-900 hover:bg-slate-100 transition-colors duration-200"
                >
                  <XLogo size={24} />
                </Link>
                <Link
                  target="_blank"
                  href="https://www.instagram.com/featcal/"
                  aria-label="Follow Featcal on Instagram"
                  className="bg-white rounded-full p-4 border border-slate-200 text-slate-900 hover:bg-slate-100 transition-colors duration-200"
                >
                  <InstagramLogoIcon size={24} />
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}
